import {Button, Form, Input, Modal} from 'antd';
import {useState} from 'react';

import {useLocale} from '../../provider/locale/locale-hook';
import {Locale} from '../../provider/locale/localization';
import {LangKeyType} from '../../provider/locale/translation/type';
import {EMAIL_REGEXP} from '../../util/regexp';
import {normalizeEmails, splitCommaSeparatedString} from '../../util/string';

type FormValuesType = {
    additionalUserEmails?: string;
};

type PropsType = {
    isOpen: boolean;
    isLoading: boolean;
    onCancel: () => void;
    onConfirm: (additionalUserEmails?: string) => void;
    titleKey: LangKeyType;
    labelKey: LangKeyType;
};

export function ExportEmailsModal(props: PropsType): JSX.Element {
    const {isOpen, isLoading, onCancel, onConfirm, titleKey, labelKey} = props;

    const [form] = Form.useForm<FormValuesType>();
    const {getLocalizedString} = useLocale();
    const [currentValue, setCurrentValue] = useState<string>('');

    async function handleOk(): Promise<void> {
        try {
            const values = await form.validateFields();
            const value = values.additionalUserEmails || '';

            if (value) {
                const emails = splitCommaSeparatedString(value).map((email) => email.trim());

                if (emails.length > 100) {
                    form.setFields([
                        {
                            name: 'additionalUserEmails',
                            errors: [
                                getLocalizedString('VALIDATION__ERROR__FIELD_COMMA_SEPARATED_LENGTH', {
                                    length: '100',
                                }),
                            ],
                        },
                    ]);

                    return;
                }

                const invalidEmails = emails.filter((email) => !EMAIL_REGEXP.test(email));

                if (invalidEmails.length > 0) {
                    form.setFields([
                        {
                            name: 'additionalUserEmails',
                            errors: [
                                getLocalizedString('VALIDATION__ERROR__FIELD_COMMA_SEPARATED_EMAILS_INCORRECT', {
                                    emails: invalidEmails.join(', '),
                                }),
                            ],
                        },
                    ]);

                    return;
                }
            }

            const normalized = normalizeEmails(value);

            // eslint-disable-next-line no-undefined
            onConfirm(normalized || undefined);
        } catch {
            // validation errors are handled by antd form
        }
    }

    return (
        <Modal
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    <Locale stringKey="POPUP__BUTTON__CANCEL" />
                </Button>,
                <Button key="ok" loading={isLoading} onClick={handleOk} type="primary">
                    <Locale stringKey="TEXT__APPLY" />
                </Button>,
            ]}
            onCancel={onCancel}
            open={isOpen}
            title={<Locale stringKey={titleKey} />}
        >
            <Form<FormValuesType> form={form} layout="vertical">
                <Form.Item label={<Locale stringKey={labelKey} />} name="additionalUserEmails">
                    <Input
                        onBlur={(event) => {
                            const normalized = normalizeEmails(event.target.value);

                            form.setFieldsValue({additionalUserEmails: normalized});
                            setCurrentValue(normalized);
                        }}
                        onChange={(event) => setCurrentValue(event.target.value)}
                        placeholder={currentValue || 'email1@example.com, email2@example.com, email3@example.com'}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
}
